import React from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Appointment } from '@uplift/api';
import { useTimezone } from '@uplift/contexts';
import { CalendarDateService, formatTimeRangeWithMeridiem } from '@uplift/services';
import { Button } from '../../../../Button';
import { LightChip } from '../../../../LightChip';
import { CancelSessionBlock } from './CancelSessionBlock.component';


const useStyles = makeStyles({ name: 'InCallUpcomingAppointmentSingleItem' })(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,

    '&:last-child': {
      borderBottom: 'none',
    }
  },
  content: {
    display: 'flex',
    alignItems: 'center',
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  title: {
    display: 'flex',
    gap: theme.spacing(1),
  }
}));


export type InCallUpcomingAppointmentSingleItemProps = {
  appointment: Appointment;
  isNext?: boolean;
  cancellable?: boolean;
  subtitle?: string;
};


export const InCallUpcomingAppointmentSingleItem: React.VFC<InCallUpcomingAppointmentSingleItemProps> = ({ appointment, isNext, cancellable, subtitle }) => {
  const [ cancelling, setCancelling ] = React.useState(false);
  const timezone = useTimezone();
  const { classes } = useStyles();

  return (
    <Box className={classes.root}>
      <Box className={classes.content}>
        <Box className={classes.details}>
          <Typography className={classes.title}>
            {CalendarDateService.format(appointment.fromDatetime, 'dddd, MMM DD', 'dddd, MMM DD, YYYY', { timezone: timezone })}
            {isNext && <LightChip label="Next session" size="small" color="info" />}
          </Typography>
          <Typography variant="body2">
            {formatTimeRangeWithMeridiem(
              CalendarDateService.date(appointment.fromDatetime).tz(timezone),
              CalendarDateService.date(appointment.toDatetime).tz(timezone)
            )}
          </Typography>
          {subtitle && (
            <Typography variant="body2">
              {subtitle}
            </Typography>
          )}
        </Box>
        {cancellable && !cancelling && (
          <Box>
            <Button size="small" onClick={() => setCancelling(true)}>CANCEL</Button>
          </Box>
        )}
      </Box>

      {cancellable && (
        <Collapse in={cancelling}>
          <CancelSessionBlock
            shown={cancelling}
            appointment={appointment}
            mode="single"
            onClose={() => setCancelling(false)}
          />
        </Collapse>
      )}
    </Box>
  );
};
